// Feed API service

import { apiBaseUrl } from '../../config/env';
import { readResponseJson } from './http';
import { appendFileToFormData } from '../../utils/uploadHelper';

const API_URL = apiBaseUrl;

export interface FeedUser {
  id: string;
  name: string | null;
  profession: string | null;
  photo: string | null;
}

export interface PollOption {
  id: string;
  text: string;
  votesCount: number;
  percentage: number;
}

export interface Poll {
  id: string;
  question: string;
  options: PollOption[];
  totalVotes: number;
  userVote?: string;
  endsAt: string | null;
}

export interface Post {
  id: string;
  type: 'text' | 'image' | 'poll' | 'link';
  userId: string;
  user: FeedUser;
  text: string | null;
  imageUrl?: string | null;
  linkUrl?: string | null;
  poll?: Poll;
  likesCount: number;
  commentsCount: number;
  sharesCount: number;
  isLiked: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface FeedResponse {
  posts: Post[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
  };
}

export interface Comment {
  id: string;
  userId: string;
  user: FeedUser;
  text: string;
  createdAt: string;
  updatedAt: string;
}

export interface CommentsResponse {
  comments: Comment[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
  };
}

// Get feed
export const getFeed = async (
  token?: string,
  page: number = 1,
  limit: number = 10
): Promise<FeedResponse> => {
  console.log('[Feed API] getFeed called:', { token: token ? 'present' : 'none', page, limit });
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const url = `${API_URL}/api/feed?page=${page}&limit=${limit}`;
  console.log('[Feed API] Fetching from:', url);

  const response = await fetch(url, { headers });
  
  console.log('[Feed API] Response status:', response.status);

  const data = await readResponseJson(response);

  if (!response.ok) {
    console.error('[Feed API] Error response:', data);
    throw new Error(data.error || 'Failed to fetch feed');
  }
  console.log('[Feed API] Success, posts count:', data.posts?.length);
  return data;
};

// Get single post
export const getPost = async (
  token: string | undefined,
  postId: string
): Promise<Post> => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_URL}/api/feed/${postId}`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get post');
  }

  return response.json();
};

// Create text post
export const createTextPost = async (
  token: string,
  text: string
): Promise<Post> => {
  const response = await fetch(`${API_URL}/api/posts/text`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ text }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create post');
  }

  return response.json();
};

// Create image post
export const createImagePost = async (
  token: string,
  imageUri: string,
  text?: string
): Promise<Post> => {
  const formData = new FormData();

  await appendFileToFormData(formData, 'image', imageUri, 'post-image.jpg', 'image/jpeg');

  if (text) {
    formData.append('text', text);
  }

  const headers: Record<string, string> = {
    'Authorization': `Bearer ${token}`,
  };

  const response = await fetch(`${API_URL}/api/posts/image`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create image post');
  }

  return response.json();
};

// Create poll post
export const createPollPost = async (
  token: string,
  poll: {
    question: string;
    options: string[];
    endsAt?: string;
  },
  text?: string
): Promise<Post> => {
  const response = await fetch(`${API_URL}/api/posts/poll`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ text, poll }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create poll post');
  }

  return response.json();
};

// Create link post
export const createLinkPost = async (
  token: string,
  linkUrl: string,
  text?: string
): Promise<Post> => {
  const response = await fetch(`${API_URL}/api/posts/link`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ text, linkUrl }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create link post');
  }

  return response.json();
};

// Like post
export const likePost = async (
  token: string,
  postId: string
): Promise<{ success: boolean; likesCount: number }> => {
  const response = await fetch(`${API_URL}/api/posts/${postId}/like`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to like post');
  }

  return response.json();
};

// Unlike post
export const unlikePost = async (
  token: string,
  postId: string
): Promise<{ success: boolean; likesCount: number }> => {
  const response = await fetch(`${API_URL}/api/posts/${postId}/like`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to unlike post');
  }

  return response.json();
};

// Get comments
export const getComments = async (
  postId: string,
  page: number = 1,
  limit: number = 20
): Promise<CommentsResponse> => {
  const response = await fetch(
    `${API_URL}/api/posts/${postId}/comments?page=${page}&limit=${limit}`
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch comments');
  }

  return response.json();
};

// Add comment
export const addComment = async (
  token: string,
  postId: string,
  text: string
): Promise<{ comment: Comment }> => {
  const response = await fetch(`${API_URL}/api/posts/${postId}/comments`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ text }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to add comment');
  }

  return response.json();
};

// Vote on poll
export const voteOnPoll = async (
  token: string,
  postId: string,
  optionId: string
): Promise<{ success: boolean; poll: Poll }> => {
  const response = await fetch(`${API_URL}/api/posts/${postId}/poll/vote`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ optionId }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to vote');
  }

  return response.json();
};

// Update post (text only)
export const updatePost = async (
  token: string,
  postId: string,
  text: string
): Promise<Post> => {
  const response = await fetch(`${API_URL}/api/posts/${postId}`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ text }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update post');
  }

  return response.json();
};

// Delete post
export const deletePost = async (
  token: string,
  postId: string
): Promise<{ success: boolean }> => {
  const response = await fetch(`${API_URL}/api/posts/${postId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete post');
  }

  return response.json();
};

// Delete comment
export const deleteComment = async (
  token: string,
  postId: string,
  commentId: string
): Promise<{ success: boolean }> => {
  const response = await fetch(`${API_URL}/api/posts/${postId}/comments/${commentId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete comment');
  }

  return response.json();
};
